import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { type SnippetAnnotation } from './cheatsheetData';

// ─── Tokenizer ────────────────────────────────────────────────────────────────

type PlainToken = { kind: 'plain'; text: string };
type AnnotatedToken = { kind: 'annotated'; text: string; tooltip: string };
type Token = PlainToken | AnnotatedToken;

/**
 * Splits `snippet` into a flat list of plain and annotated tokens.
 * Annotations are matched left-to-right; each `text` is consumed once in the
 * order it appears in the `annotations` array, so duplicate substrings can be
 * annotated independently by listing them multiple times.
 */
function tokenize(snippet: string, annotations: SnippetAnnotation[]): Token[] {
  if (!annotations.length) return [{ kind: 'plain', text: snippet }];

  // Build a queue: for each annotation record how many times we've consumed it
  // (simple approach — find all occurrences in order and assign them one-to-one)
  type Marker = { start: number; end: number; tooltip: string };
  const markers: Marker[] = [];

  // Track consumed positions so two annotations can't claim the same character
  const consumed = new Set<number>();

  for (const ann of annotations) {
    // Find first occurrence not already consumed
    let searchFrom = 0;
    while (searchFrom < snippet.length) {
      const idx = snippet.indexOf(ann.text, searchFrom);
      if (idx === -1) break;
      // Check none of these positions are already claimed
      let clash = false;
      for (let i = idx; i < idx + ann.text.length; i++) {
        if (consumed.has(i)) {
          clash = true;
          break;
        }
      }
      if (!clash) {
        for (let i = idx; i < idx + ann.text.length; i++) consumed.add(i);
        markers.push({
          start: idx,
          end: idx + ann.text.length,
          tooltip: ann.tooltip,
        });
        break;
      }
      searchFrom = idx + 1;
    }
  }

  // Sort markers by start position
  markers.sort((a, b) => a.start - b.start);

  // Build token list
  const tokens: Token[] = [];
  let pos = 0;
  for (const m of markers) {
    if (m.start > pos) {
      tokens.push({ kind: 'plain', text: snippet.slice(pos, m.start) });
    }
    tokens.push({
      kind: 'annotated',
      text: snippet.slice(m.start, m.end),
      tooltip: m.tooltip,
    });
    pos = m.end;
  }
  if (pos < snippet.length) {
    tokens.push({ kind: 'plain', text: snippet.slice(pos) });
  }
  return tokens;
}

// ─── Component ────────────────────────────────────────────────────────────────

type Props = {
  snippet: string;
  annotations?: SnippetAnnotation[];
};

export function AnnotatedSnippet({ snippet, annotations = [] }: Props) {
  const tokens = tokenize(snippet, annotations);

  return (
    <pre className="flex-1 overflow-x-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed text-foreground">
      {tokens.map((token, i) => {
        if (token.kind === 'plain') {
          return <span key={i}>{token.text}</span>;
        }
        return (
          <Tooltip key={i} delayDuration={120}>
            <TooltipTrigger asChild>
              <span className="cursor-help rounded-sm underline decoration-muted-foreground/60 decoration-dotted underline-offset-2 transition-colors hover:bg-primary/10 hover:text-primary">
                {token.text}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-52 text-center text-xs">
              {token.tooltip}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </pre>
  );
}
